import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    ElementRef,
    Input,
    OnInit,
    TemplateRef,
    ViewChild,
    ComponentFactoryResolver,
    ApplicationRef,
    Injector,
    Inject,
    Renderer2
} from '@angular/core';
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import { CalendarOptions } from '@fullcalendar/angular';
import { ApiService } from 'src/app/services/api/api.service';
import { A } from '@angular/cdk/keycodes';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

import { PLATFORM_ID } from '@angular/core';
import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import { Title, Meta } from '@angular/platform-browser';
import { ActionService } from 'src/app/services/action/action.service';

@Component({
    template: `
    <div class="fc-content" [ngbPopover]="template" container="body" triggers="manual">
      <ng-content></ng-content>
    </div>
  `,
})
export class PopoverWrapperComponent {
    template: TemplateRef<any>;

    @ViewChild(NgbPopover, { static: true }) popover: NgbPopover;
}

@Component({
    selector: 'app-timetable',
    templateUrl: './timetable.component.html',
    styleUrls: ['./timetable.component.scss']
})

export class TimetableComponent implements OnInit {

    // Calendar Configuration

    calendarOptions: CalendarOptions = {
        initialView: 'dayGridMonth',
        //eventMouseEnter: this.handleDateClick.bind(this), // bind is important!
        firstDay: 1,
        events: [],
        displayEventTime: false,
        eventDidMount: this.renderTooltip.bind(this),
        eventWillUnmount: this.destroyTooltip.bind(this),
        eventMouseEnter: this.showPopover.bind(this),
        eventMouseLeave: this.hidePopover.bind(this),
        contentHeight: "auto",
        handleWindowResize: true,
    };
    calendarPlugins = [dayGridPlugin];

    // Calendar Related

    weekendEvents: any = null;
    weekDayEvents: any = null;
    allEvents: any = null;
    filteredEvents: any = null;
    error: boolean = false;
    localSubjects: any;
    subjectsSelect: any = null;
    allSubjects: any = null;
    courses: any = [];
    looper: any;
    selectedCourse: any = 2;
    loading: boolean = true;

    // Popover

    @ViewChild('popoverTmpl', { static: true }) popoverTmpl: TemplateRef<any>;
    popoversMap = new Map<any, ComponentRef<PopoverWrapperComponent>>();
    popoverFactory = this.resolver.resolveComponentFactory(PopoverWrapperComponent);

    regions: any;
    selectedRegion: any;
    levels: any = [];
    selectedLevel: any;
    subjects: any = [];
    selectedSubject: any;
    endEvents: any = [];

    timetableOrList: any = 'timetable';
    weekday: boolean = true;
    weekend: boolean = true;

    sessionFilter: boolean = true;
    examFilter: boolean = true;
    eventFilter: boolean = true;
    isBrowser: boolean = false;
    localCourses: any = [];

    // Page Builder

    loaded: boolean = false;
    content: any = {};
    slug: string = 'timetable';
    requestedUrl: any = '';
    page: any = {};
    sidemenu_links: any = [];

    constructor(
        private api: ApiService,
        private cd: ChangeDetectorRef,
        private resolver: ComponentFactoryResolver,
        private injector: Injector,
        private appRef: ApplicationRef,
        private route: ActivatedRoute,
        private renderer: Renderer2,
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) platformId: Object,
        private titleService: Title,
        private metaTagService: Meta,
        private router: Router,
        private actionService: ActionService
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    ngOnInit(): void {

        this.requestedUrl = this.router.url;

        this.actionService.setAction({
            action: "gtm",
            data: {
                url: this.requestedUrl,
                title: "Timetable"
            }
        });

        // If currently viewing draft then fetch draft page builder otherwise fetch as normal

        if (this.router.url.includes('draft')) {
            this.get_page_builder_draft();
        } else {
            this.get_page_builder();
        }

        this.fetchRegions();
        this.fetchCourses();

    }

    /**
     * Fetches page builder content based on the slug
     */
    async get_page_builder() {

        await this.api.fetch_page_builder(this.slug)
            .subscribe(res => {

                // Page SEO

                if (res?.data?.item?.seo?.title) {
                    this.titleService.setTitle(res?.data?.item?.seo?.title);
                    this.metaTagService.updateTag({ property: 'og:title', content: res?.data?.item?.seo?.title });
                }

                if (res?.data?.item?.seo?.description) {
                    this.metaTagService.updateTag(
                        { name: 'description', content: res?.data?.item?.seo?.description }
                    );
                    this.metaTagService.updateTag({ property: 'og:description', content: res?.data?.item?.seo?.description });
                }

                if (res?.data?.item?.seo?.schema) {
                    const seoSchema = res?.data?.item?.seo?.schema;
                    const scripts = this.document.querySelectorAll(`script[type="application/ld+json"]`);
                    const scriptTypeExists = (scripts.length > 0);
                    if (!scriptTypeExists) {
                        const script = this.renderer.createElement('script');
                        script.type = 'application/ld+json';
                        this.renderer.setProperty(script, 'innerHTML', seoSchema);
                        this.renderer.appendChild(this.document.head, script);
                    }
                }

                if (res?.data?.item?.seo?.keywords) {
                    this.metaTagService.updateTag(
                        { name: 'keywords', content: res?.data?.item?.seo?.keywords }
                    );
                }

                // Page Content

                this.page = res.data.item;
                this.content = JSON.stringify(res.data.item);
                this.loaded = true;
                this.cd.detectChanges();
            }, (err) => {
                this.loaded = true;
                this.cd.detectChanges();
            });

    }

    /**
     * Fetches draft page builder content based on the slug
     */
    async get_page_builder_draft() {

        await this.api.fetch_page_builder_draft(this.slug)
            .subscribe(res => {
                this.page = res.data.item;
                this.content = JSON.stringify(res.data.item);
                this.loaded = true;
                this.cd.detectChanges();
            }, (err) => {
                this.loaded = true;
                this.cd.detectChanges();
            });

    }

    /**
     * Initial content fetch
     */
    async fetchRegions() {

        await this.api.get_region_list()
            .subscribe(res => {
                let defaultRegion = {
                    RegionKey: {
                        _text: null
                    },
                    RegionName: {
                        _text: "All Regions"
                    },
                };

                this.selectedRegion = defaultRegion;

                if (res?.Result?.Regions?.Region) {
                    res?.Result?.Regions?.Region.unshift(defaultRegion);
                    this.regions = res?.Result?.Regions?.Region;
                } else {
                    this.regions = [];
                }
            }, (err) => {
                console.log(err);
                this.error = true;
            });

    }

    /**
     * Fetch all courses.
     * This data is then passed to parseRawData function which in turn is used to build the sidemenu
     */
    async fetchCourses(courseId = null) {
        this.loading = true;

        await this.api.fetch_institutions()
            .subscribe(res => {
                this.localCourses = res.data.items;

                this.api.fetch_course_list()
                    .subscribe(res => {

                        this.courses = res.Result.Course;

                        this.courses.forEach(course => {
                            let index = this.localCourses.findIndex(x => x.identifier == course.CourseKey._text);
                            if (index !== -1) {
                                course.Cms = this.localCourses[index];
                            } else {
                                course.Cms = {};
                            }
                        });

                        this.courses.sort(function (a, b) {
                            if (a.Cms.position < b.Cms.position)
                                return -1;
                            if (a.Cms.position > b.Cms.position)
                                return 1;
                            return 0;
                        });

                        let id = 'noId';
                        this.route.paramMap.subscribe(params => {
                            if (params.has("id")) {
                                id = params.get("id");
                            }
                        });

                        let selectedIndex = 0;
                        if (courseId) {
                            id = courseId;
                        }

                        // If we have a pre-selected course from params

                        if (id != 'noId') {
                            this.courses.forEach((course, i) => {
                                if (course?.CourseKey?._text == id) {
                                    selectedIndex = i;
                                }
                            });
                        } else {
                            selectedIndex = 0;
                        }

                        this.selectedCourse = res.Result.Course[selectedIndex];
                        this.levels = this.ensureArray(this.selectedCourse?.SubjectLevels?.SubjectLevel);

                        this.levels.forEach(level => {
                            this.localCourses.forEach(course => {
                                let index = course.levels.findIndex(x => x.identifier == level.SubjectLevelKey._text);

                                if (index !== -1) {
                                    level.SubjectLevelName._text = course.levels[index].title;
                                    level.Priority = course.levels[index].priority;
                                }
                            });
                        });

                        this.selectedLevel = 'default';
                        this.fetchSubjects();
                    }, (err) => {
                        this.error = true;
                    });

                //below
            }, (err) => {
                this.error = true;
            });

    }

    /**
   * Fetch all subjects from cms.
   * This data is then used to fill out the content in the filters
   */
    async fetchSubjects() {
        await this.api.fetch_subjects(this.selectedCourse.CourseKey._text)
            .subscribe(res => {
                this.localSubjects = res.data.items;

                this.subjects = this.ensureArray(res.data.items);
                this.allSubjects = this.subjects;
                this.selectedSubject = 'default';

                this.fetchCalendarNew();
            }, (err) => {
                this.error = true;
            });

    }


    async fetchCalendarNew() {
        let regionKey = null;
        if (this.selectedRegion?.RegionKey?._text) {
            regionKey = this.selectedRegion?.RegionKey?._text;
        }
        let levelKey = null;
        if (this.selectedLevel?.SubjectLevelName?._text) {
            levelKey = this.selectedLevel?.SubjectLevelKey?._text;
        }
        let subjectKey = null;
        if (this.selectedSubject?.title) {
            subjectKey = this.selectedSubject?.identifier;
        }



        await this.api.fetch_timetable2(this.selectedCourse.CourseKey._text, regionKey, levelKey, subjectKey)
            .subscribe(res => {

                if (res?.Result?.Items) {
                    this.allEvents = this.ensureArray(res?.Result?.Items?.Item);
                } else {
                    this.allEvents = [];
                    this.loading = false;
                }
                this.populateCalendarNew();
            }, (err) => {

            });
    }



    populateCalendarNew() {
        let events = [];
        this.endEvents = [];
        this.allEvents.forEach((calendarItem, i) => {

            if ((calendarItem?.ItemType?._text == "SESSION" && this.sessionFilter) ||
                (calendarItem?.ItemType?._text == "EXAM" && this.examFilter) ||
                (calendarItem?.ItemType?._text == "EVENT" && this.eventFilter)
            ) {

                let times = calendarItem?.Time?._text;
                let startTime = null;
                let endTime = null;
                let displayStart = null;
                let displayEnd = null;
                let type = calendarItem?.ItemType?._text;


                let date = calendarItem?.ItemDate?._text;
                if (calendarItem?.ItemDate?._text.includes("BR")) {
                    date = calendarItem?.ItemDate?._text.split('<BR>')[0];
                }

                if (times) {
                    times = times.split('<br>');
                    times = times[0].toLowerCase().replace('saturday', '').replace('sunday', '').replace('sat', '').replace('&', '').replace('sun', '').replace('monday', '').replace('tuesday', '').replace('wednesday', '').replace('thursday', '').replace('friday', '').replace('  ', ' ').replace('  ', ' ');
                    times = times.split(' ');
                    if (times[1]) {
                        displayStart = times[1];
                        startTime = this.convertToTime(times[1]);
                    }
                    if (times[3]) {
                        displayEnd = times[3];
                        endTime = this.convertToTime(times[3]);
                    }
                }


                let beforeSimpleDate = date.split('.');

                let simpleStartDate = beforeSimpleDate[2] + '-' + beforeSimpleDate[1] + '-' + beforeSimpleDate[0];
                let startDate = new Date(simpleStartDate);
                let endDate = new Date(simpleStartDate);

                let isWeekend = false;
                if (startDate.getDay() == 0 || startDate.getDay() == 6) {
                    isWeekend = true;
                }
                let title = calendarItem?.ItemCaption?._text;


                let formattedItem = {
                    id: i,
                    title: title,
                    start: startDate,
                    end: endDate,
                    display: 'block',
                    venueName: calendarItem?.Venue?._text,
                    subjectName: calendarItem?.Name?._text,
                    subjectLevelName: calendarItem?.Level?._text,
                    subjectCode: calendarItem?.Code?._text,
                    time: calendarItem?.Time?._text,
                    simpleStartDate: simpleStartDate,
                    isWeekend: isWeekend,
                    type: type,
                    color: '#' + calendarItem?.ItemColour?._text,
                };


                if ((this.weekday && this.weekend) || (isWeekend && this.weekend) || (!isWeekend && this.weekday)) {
                    events.push(formattedItem);
                }
            }

        });

        this.calendarOptions.events = events;

        this.endEvents = this.populateListNew(events);
        this.loading = false;
        this.cd.detectChanges();

    }

    populateListNew(events) {

        // this gives an object with dates as keys
        const groups = events.reduce((groups, item) => {
            const date = item.simpleStartDate;
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(item);
            return groups;
        }, {});

        // Edit: to add it in the array format instead
        const groupArrays = Object.keys(groups).map((date) => {
            return {
                date,
                items: groups[date]
            };
        });

        groupArrays.sort((x, y) => +new Date(x.date) - +new Date(y.date));


        return groupArrays;

    }




    convertToTime(time) {
        let adder = 0;
        if (time.includes('pm')) {
            adder = 12;
        }
        time = parseInt(time.replace('am', '').replace('pm', ''));
        time = time + adder;
        time = time.toString() + ':00:00';
        if (adder == 0) {
            time = '0' + time;
        }

        return time;
    }

    handleDateClick($event) {
        $event.el.title = 'asd';
    }

    /**
     * Helper to make sure that the response is an array
     * @param possibleArray
     * @returns
     */
    ensureArray(possibleArray) {
        if (possibleArray) {
            if (Array.isArray(possibleArray)) {
                return possibleArray;
            } else {
                return [possibleArray];
            }
        } else {
            return [];
        }
    }

    renderTooltip(event) {
        const projectableNodes = Array.from(event.el.childNodes)

        const compRef = this.popoverFactory.create(this.injector, [projectableNodes], event.el);
        compRef.instance.template = this.popoverTmpl;

        this.appRef.attachView(compRef.hostView)
        this.popoversMap.set(event.el, compRef)
    }

    destroyTooltip(event) {
        const popover = this.popoversMap.get(event.el);
        if (popover) {
            this.appRef.detachView(popover.hostView);
            popover.destroy();
            this.popoversMap.delete(event.el);
        }
    }

    showPopover(event) {
        const popover = this.popoversMap.get(event.el);
        if (popover) {
            popover.instance.popover.open({ event: event.event });
        }
    }


    hidePopover(event) {
        const popover = this.popoversMap.get(event.el);
        if (popover) {
            popover.instance.popover.close();
        }
    }


    coursesChanged(event: MatSelectChange) {

        // We only care if the user has made the selection
        if (event['isUserInput']) {
            this.loading = true;
            this.selectedCourse = event.source.value;
            this.fetchCourses(this.selectedCourse.CourseKey._text);
        }

    }


    regionsChanged(event: MatSelectChange) {

        // We only care if the user has made the selection
        if (event['isUserInput']) {
            this.selectedRegion = event.source.value;
            this.loading = true;
            this.fetchSubjects();
        }
    }


    levelsChanged(event: MatSelectChange) {

        // We only care if the user has made the selection
        if (event['isUserInput']) {
            this.selectedSubject = 'default';
            this.selectedLevel = event.source.value;

            if (event.source.value == 'default') {
                this.subjects = this.allSubjects;
            } else {
                let subjectsKeys = [];
                this.ensureArray(this.selectedLevel?.Subjects?.Subject).forEach(subject => {
                    subjectsKeys.push(parseInt(subject?.SubjectKey?._text));
                });
                let subjects = [];
                this.allSubjects.forEach(element => {
                    if (subjectsKeys.includes(element?.identifier)) {
                        subjects.push(element);
                    }
                });

                this.subjects = subjects;
            }

            this.fetchCalendarNew();
        }

    }

    subjectsChanged(event: MatSelectChange) {

        // We only care if the user has made the selection
        if (event['isUserInput']) {
            this.selectedSubject = event.source.value;
            this.fetchCalendarNew();
        }

    }

    sessionFilterChange(event) {
        this.populateCalendarNew();
    }


    examFilterChange(event) {
        this.populateCalendarNew();
    }

    eventFilterChange(event) {
        this.populateCalendarNew();
    }


    formatDate(date) {

        date = new Date(date);
        var monthNames = [
            "Jan", "Feb", "Mar",
            "Apr", "May", "Jun", "Jul",
            "Aug", "Sep", "Oct",
            "Nov", "Dec"
        ];
        var day = date.getDate();
        if (day < 10) {
            day = `0${day}`;
        }


        var monthIndex = date.getMonth();
        var year = date.getFullYear();
        return day + ' ' + monthNames[monthIndex] + ' ' + year;
    }

    toggleWeek() {
        this.weekday = !this.weekday;
        this.populateCalendarNew();
    }

    toggleWeekend() {
        this.weekend = !this.weekend;
        this.populateCalendarNew();
    }

}
